// React
import { ComponentProps, ReactElement } from 'react';
// Libraries
import { Divider, Flex, Skeleton, Spacer, Text, Tooltip } from '@chakra-ui/react';
// Components
import { LabelTooltip } from '@atoms/LabelTooltip';
import { RatingInfoCompacted, RatingTextTooltip } from '@molecules/index';
// Types
import { TooltipDecoratorKeys, TooltipDecoratorMap, TooltipDecoratorProps } from './TooltipDecorator.type';

/**
 * Map of available TooltipDecorator components.
 */
export const TooltipDecoratorTypes = {
  RatingInfoCompacted,
  RatingTextTooltip,
  LabelTooltip,
};

/**
 * Enum of available TooltipDecorator components.
 */
export enum TooltipDecoratorTypesEnum {
  RatingInfoCompacted = 'RatingInfoCompacted',
  RatingTextTooltip = 'RatingTextTooltip',
  LabelTooltip = 'LabelTooltip',
}

/**
 * TooltipDecorator Component
 *
 * The TooltipDecorator component decorates a component with a tooltip based on the provided type.
 * It renders the decorated component within a tooltip container, displaying a title and the decorated component.
 *
 * @template T - The type of the tooltip.
 *
 * @component
 * @param {TooltipDecoratorProps<T>} props - The component props.
 * @param {TooltipDecoratorKeys} props.type - The type of the tooltip.
 * @param {TooltipDecoratorComponentProps<T>} props.params - The props specific to the selected tooltip component.
 * @param {string} props.title - The title for the tooltip.
 * @param {PositionObject} props.positionObject - The position of the tooltip.
 * @param {boolean} [props.isLoading=false] - Determines whether the tooltip is in a loading state.
 *
 * @returns {ReactElement} The rendered decorated component.
 */
export const TooltipDecorator = <T extends TooltipDecoratorKeys>({
  type,
  params,
  title,
  positionObject,
  isLoading = false,
}: TooltipDecoratorProps<T>) => {
  const element = TooltipDecoratorTypes[type] as (props: ComponentProps<TooltipDecoratorMap[T]>) => ReactElement;
  return (
    <svg>
      <g style={{ pointerEvents: 'none' }}>
        <foreignObject {...positionObject} width='90' height='60'>
          <Tooltip
            hasArrow
            isOpen
            placement='top'
            bg='white'
            variant='tooltip'
            gutter={0}
            label={
              <Flex direction='column' alignItems='center'>
                <Skeleton isLoaded={!isLoading} fitContent>
                  <Text variant='faded' fontSize={{ base: 'xs', sm: 'sm' }}>
                    {title}
                  </Text>
                </Skeleton>
                <Divider borderColor='gray.200' />
                {element({ ...params })}
              </Flex>
            }
          >
            <Spacer />
          </Tooltip>
        </foreignObject>
      </g>
    </svg>
  );
};
